import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage.tsx';
import TestContainer from './components/TestContainer.tsx';
import ResultsPage from './components/ResultsPage.tsx';
import { auth } from './firebaseConfig.ts';
import { onAuthStateChanged } from 'firebase/auth';
import { DEBUG } from './config';
import { loadDndStructureForBrowser } from './utils/dndLoader';

// --- DevtoolsPanel для диагностики ---
function DevtoolsPanel() {
  const [log, setLog] = React.useState<any[]>([]);
  const [last, setLast] = React.useState<any>(null);
  React.useEffect(() => {
    const update = () => {
      setLog(window._psyco_debug_log ? [...window._psyco_debug_log] : []);
      const debugInfo = window._psyco_debug;
      setLast(debugInfo && typeof debugInfo === 'object' ? { ...debugInfo } : debugInfo);
    };
    update();
    const int = setInterval(update, 1000);
    return () => clearInterval(int);
  }, []);
  // Показываем панель только если включен флаг отладки
  if (!DEBUG.SHOW_DEBUG_PANEL) return null;
  return (
    <div style={{ position: 'fixed', bottom: 0, right: 0, width: 400, maxHeight: 400, overflow: 'auto', background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: 12, zIndex: 9999, borderRadius: 8, padding: 8 }}>
      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>PSYCO DEBUG PANEL</div>
      <div><b>Последний:</b> <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(last, null, 2)}</pre></div>
      <div><b>Лог ({log.length}):</b> <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(log.slice(-10), null, 2)}</pre></div>
    </div>
  );
}

// --- ErrorBoundary для глобального отлова ошибок ---
type ErrorBoundaryState = { error: any, errorInfo: any };
type ErrorBoundaryProps = { children?: React.ReactNode };
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = { children: null };
  state: ErrorBoundaryState = { error: null, errorInfo: null };
  static getDerivedStateFromError(error: any) {
    return { error, errorInfo: null };
  }
  componentDidCatch(error: any, errorInfo: any) {
    // @ts-expect-error TS неверно определяет типы, setState есть у React.Component
    this.setState({ error, errorInfo });
  }
  render() {
    if (this.state && this.state.error) {
      return (
        <div style={{ padding: 32, color: 'red' }}>
          <h2>GLOBAL ERROR BOUNDARY</h2>
          <pre style={{ background: '#fee', padding: 16, borderRadius: 8, maxWidth: 900, overflowX: 'auto' }}>
            {this.state.error && this.state.error.toString()}
            {'\n'}
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </pre>
          <h3>window._psyco_debug_log:</h3>
          <pre style={{ background: '#eee', padding: 16, borderRadius: 8, maxWidth: 900, overflowX: 'auto' }}>
            {typeof window !== 'undefined' ? JSON.stringify(window._psyco_debug_log, null, 2) : ''}
          </pre>
        </div>
      );
    }
    // @ts-expect-error TS неверно определяет типы, props есть у React.Component
    return this.props.children;
  }
}

function App() {
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [criticalError, setCriticalError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize DnD structure for browser
    loadDndStructureForBrowser();
    
    // Проверка на глобальную ошибку, выставленную в index.tsx
    if (typeof window !== 'undefined' && (window as any).CRITICAL_STARTUP_ERROR) {
      setCriticalError((window as any).CRITICAL_STARTUP_ERROR);
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsFirebaseReady(true);
      unsubscribe();
    });
    return () => unsubscribe();
  }, []);

  if (criticalError) {
    return (
      <div style={{ color: 'red', padding: 32, fontSize: 20, fontFamily: 'monospace' }}>
        {criticalError}
      </div>
    );
  }

  // --- Новый компонент-обёртка для доступа к location ---
  function AppWithLocation() {
    const location = useLocation();
    // Проверяем, находимся ли мы на странице результатов
    const isResultsPage = location.pathname.startsWith('/results');
    return (
      <div className="App">
        {isFirebaseReady ? (
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/test" element={<TestContainer />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/results/:resultId" element={<ResultsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <div>Initializing...</div>
        )}
        {/* Показываем DevtoolsPanel только если НЕ страница результатов */}
        {!isResultsPage && <DevtoolsPanel />}
      </div>
    );
  }

  return (
    <Router>
      <ErrorBoundary>
        <AppWithLocation />
      </ErrorBoundary>
    </Router>
  );
}

export default App;
